import {
  Activity,
  BarChart3,
  Bot,
  Building2,
  CalendarDays,
  FileBarChart,
  FileText,
  FolderOpen,
  Home,
  Inbox,
  LineChart,
  ListChecks,
  Stethoscope,
  Mail,
  Users,
  User,
  UserCog,
  UserPlus,
  Wallet,
  WalletCards,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type RoleKey =
  | "facilityNonMedical"
  | "facilityMedical"
  | "providerOperations"
  | "providerMedical"
  | "providerExecutive"
  | "billingAdmin";

export type DashboardLink = {
  key: string;
  name: string;
  path: string;
  icon: LucideIcon;
};

export type NavChildLink = {
  title: string;
  path: string;
};

export type NavItemConfig = {
  title: string;
  icon?: LucideIcon;
  path?: string;
  children?: NavChildLink[];
};

export type NavSectionConfig = {
  label: string;
  items: NavItemConfig[];
};

export type RoleEntityOption = {
  id: string;
  label: string;
  typeTitle: string;
  displayName: string;
  icon?: LucideIcon;
};

export type RoleEntityGroup = {
  label: string;
  options: RoleEntityOption[];
};

export type RoleEntityConfig = {
  initialOptionId: string;
  groups: RoleEntityGroup[];
};

export type RoleConfig = {
  key: RoleKey;
  label: string;
  dashboards: DashboardLink[];
  sections: NavSectionConfig[];
  entity: RoleEntityConfig;
};

const sharedRequestCreationItems: NavItemConfig[] = [
  {
    title: "New Patient",
    icon: UserPlus,
    children: [{ title: "Patient Referral", path: "/facility-user-bh" }],
  },
  {
    title: "Existing Patient",
    icon: UserCog,
    children: [
      { title: "Medical Records", path: "/existing-patient/medical-records" },
      { title: "Status Change", path: "/existing-patient/status-change" },
      { title: "Visit", path: "/existing-patient/visit" },
      { title: "Lab Order", path: "/existing-patient/lab-order" },
      { title: "Refill Order", path: "/existing-patient/refill-order" },
    ],
  },
  {
    title: "Other",
    icon: ListChecks,
    children: [{ title: "General", path: "/other/general" }],
  },
];

const facilityDashboards: DashboardLink[] = [
  { key: "newHome", name: "Home", path: "/new-home", icon: Home },
  { key: "census", name: "Census & Schedule", path: "/census-schedules", icon: CalendarDays },
  { key: "requests", name: "Requests", path: "/requests", icon: Inbox },
  { key: "cases", name: "Cases", path: "/cases", icon: FolderOpen },
  { key: "encounters", name: "Encounters (Visits)", path: "/encounters", icon: Stethoscope },
];

const [newHomeDashboard, censusDashboard, requestsDashboard, casesDashboard] = facilityDashboards;

const facilityNonMedicalDashboards: DashboardLink[] = [
  newHomeDashboard,
  { key: "commsCenter", name: "Comms Center", path: "/comms-center", icon: Mail },
  censusDashboard,
  requestsDashboard,
  casesDashboard,
];

const entityOptions = {
  dsbcWest: {
    id: "dsbc-west",
    label: "DSBC Phoenix: West Campus, BLDG A",
    typeTitle: "Referring Facility",
    displayName: "DSBC West Campus, BLDG A",
    icon: Building2,
  },
  dsbcEast: {
    id: "dsbc-east",
    label: "DSBC Scottsdale: East Campus",
    typeTitle: "Referring Facility",
    displayName: "DSBC Scottsdale: East Campus",
    icon: Building2,
  },
  alphaGlendale: {
    id: "alpha-glendale",
    label: "Alpha Skilled, Glendale",
    typeTitle: "Referring Facility",
    displayName: "Alpha Skilled, Glendale",
    icon: Building2,
  },
  vaFlagstaff: {
    id: "va-flagstaff",
    label: "VA Flagstaff",
    typeTitle: "Referring Facility",
    displayName: "VA Flagstaff",
    icon: Building2,
  },
  vaTuscon: {
    id: "va-tuscon",
    label: "VA Tuscon",
    typeTitle: "Referring Facility",
    displayName: "VA Tuscon",
    icon: Building2,
  },
  happyHomesMesa: {
    id: "happy-homes-mesa",
    label: "Happy Homes: Mesa AZ",
    typeTitle: "Referring Facility",
    displayName: "Happy Homes: Mesa AZ",
    icon: Building2,
  },
  sadHomesGila: {
    id: "sad-homes-gila",
    label: "Sad Homes: Gila Bend AZ",
    typeTitle: "Referring Facility",
    displayName: "Sad Homes: Gila Bend AZ",
    icon: Building2,
  },
  customVaHomes: {
    id: "custom-va-homes",
    label: "VA Homes (AZ)",
    typeTitle: "Custom Group",
    displayName: "VA Homes (AZ)",
    icon: Users,
  },
  customAlfNe: {
    id: "custom-alf-ne",
    label: "ALF Homes NE Valley (AZ)",
    typeTitle: "Custom Group",
    displayName: "ALF Homes NE Valley (AZ)",
    icon: Users,
  },
  customAlfSw: {
    id: "custom-alf-sw",
    label: "ALF Homes SW Valley (AZ)",
    typeTitle: "Custom Group",
    displayName: "ALF Homes SW Valley (AZ)",
    icon: Users,
  },
  customAllPerforming: {
    id: "custom-all-performing",
    label: "All Performing Sites",
    typeTitle: "Custom Group",
    displayName: "All Performing Sites",
    icon: Users,
  },
};

const dsbcGroup: RoleEntityGroup = {
  label: "Desert Springs Behavioral Center",
  options: [entityOptions.dsbcWest, entityOptions.dsbcEast],
};

const alphaGroup: RoleEntityGroup = {
  label: "Alpha Skilled Nursing Facility",
  options: [entityOptions.alphaGlendale],
};

const vaFlagstaffGroup: RoleEntityGroup = {
  label: "VA Facility (Flagstaff)",
  options: [entityOptions.vaFlagstaff],
};

const vaTusconGroup: RoleEntityGroup = {
  label: "VA Facility (Tuscon)",
  options: [entityOptions.vaTuscon],
};

const happyHomesGroup: RoleEntityGroup = {
  label: "Happy Homes Assisted Living",
  options: [entityOptions.happyHomesMesa],
};

const sadHomesGroup: RoleEntityGroup = {
  label: "Sad Homes Assisted Living",
  options: [entityOptions.sadHomesGila],
};

const facilityEntityConfig: RoleEntityConfig = {
  initialOptionId: entityOptions.dsbcWest.id,
  groups: [dsbcGroup],
};

const facilityMedicalEntityConfig: RoleEntityConfig = {
  initialOptionId: entityOptions.dsbcWest.id,
  groups: [dsbcGroup],
};

const providerOperationsEntityConfig: RoleEntityConfig = {
  initialOptionId: entityOptions.customAlfNe.id,
  groups: [
    dsbcGroup,
    alphaGroup,
    vaFlagstaffGroup,
    vaTusconGroup,
    happyHomesGroup,
    sadHomesGroup,
    {
      label: "Custom Groups",
      options: [entityOptions.customVaHomes, entityOptions.customAlfNe, entityOptions.customAlfSw],
    },
  ],
};

const providerMedicalEntityConfig: RoleEntityConfig = {
  initialOptionId: entityOptions.vaFlagstaff.id,
  groups: [
    dsbcGroup,
    alphaGroup,
    vaFlagstaffGroup,
    vaTusconGroup,
    happyHomesGroup,
    sadHomesGroup,
  ],
};

const providerExecutiveEntityConfig: RoleEntityConfig = {
  initialOptionId: entityOptions.customAllPerforming.id,
  groups: [
    dsbcGroup,
    alphaGroup,
    vaFlagstaffGroup,
    vaTusconGroup,
    happyHomesGroup,
    sadHomesGroup,
    {
      label: "Custom Groups",
      options: [entityOptions.customVaHomes, entityOptions.customAlfNe, entityOptions.customAlfSw, entityOptions.customAllPerforming],
    },
  ],
};

const billingAdminEntityConfig: RoleEntityConfig = {
  initialOptionId: entityOptions.customVaHomes.id,
  groups: [
    dsbcGroup,
    alphaGroup,
    vaFlagstaffGroup,
    vaTusconGroup,
    happyHomesGroup,
    sadHomesGroup,
    {
      label: "Custom Groups",
      options: [entityOptions.customVaHomes, entityOptions.customAllPerforming],
    },
  ],
};

export const ROLE_CONFIGS: Record<RoleKey, RoleConfig> = {
  facilityNonMedical: {
    key: "facilityNonMedical",
    label: "Facility (Non-Medical)",
    dashboards: facilityNonMedicalDashboards,
    sections: [
      {
        label: "Request Creation",
        items: sharedRequestCreationItems,
      },
    ],
    entity: facilityEntityConfig,
  },
  facilityMedical: {
    key: "facilityMedical",
    label: "Facility (Medical)",
    dashboards: facilityDashboards,
    sections: [
      {
        label: "Request Creation",
        items: sharedRequestCreationItems,
      },
    ],
    entity: facilityMedicalEntityConfig,
  },
  providerOperations: {
    key: "providerOperations",
    label: "Provider Operations (Admin)",
    dashboards: [
      ...facilityDashboards,
      { key: "claims", name: "Claims & Billing", path: "/claims-billing", icon: FileText },
      { key: "metrics", name: "Metrics", path: "/metrics", icon: BarChart3 },
      { key: "staff", name: "Staff and Facilities", path: "/staff-facilities", icon: Users },
    ],
    sections: [
      {
        label: "Request Creation",
        items: [
          ...sharedRequestCreationItems.slice(0, 2),
          {
            title: "Payer Source",
            icon: Wallet,
            children: [{ title: "Medical Records Request", path: "/payer-source/medical-records-request" }],
          },
          sharedRequestCreationItems[2],
        ],
      },
      {
        label: "Entity Creation",
        items: [
          {
            title: "New Facility",
            icon: Building2,
            children: [{ title: "Referring Facility", path: "/entity/new-facility/referring-facility" }],
          },
          {
            title: "New Provider",
            icon: Stethoscope,
            children: [{ title: "Medical Provider", path: "/entity/new-provider/medical-provider" }],
          },
          {
            title: "New Individual",
            icon: User,
            children: [{ title: "Contact", path: "/entity/new-individual/contact" }],
          },
        ],
      },
    ],
    entity: providerOperationsEntityConfig,
  },
  providerMedical: {
    key: "providerMedical",
    label: "Provider Medical",
    dashboards: [
      ...facilityDashboards,
      { key: "billingIncome", name: "Billing and Income", path: "/billing-income", icon: Wallet },
    ],
    sections: [
      {
        label: "Request Creation",
        items: [
          {
            title: "New Patient",
            icon: UserPlus,
            children: [{ title: "Patient Referral (on behalf)", path: "/provider/on-behalf-patient-referral" }],
          },
          ...sharedRequestCreationItems.slice(1),
        ],
      },
    ],
    entity: providerMedicalEntityConfig,
  },
  providerExecutive: {
    key: "providerExecutive",
    label: "Provider Executive",
    dashboards: [
      newHomeDashboard,
      censusDashboard,
      { key: "billingIncome", name: "Billing and Income", path: "/billing-income", icon: Wallet },
      { key: "profitLoss", name: "Profit and Loss", path: "/profit-loss", icon: LineChart },
      { key: "forecasting", name: "Forecasting", path: "/forecasting", icon: Activity },
    ],
    sections: [
      {
        label: "Request Creation",
        items: [
          {
            title: "New Report",
            icon: FileBarChart,
            children: [
              { title: "Ad-Hoc (oneshot) Analysis", path: "/reports/ad-hoc-analysis" },
              { title: "Recurring Analysis", path: "/reports/recurring-analysis" },
            ],
          },
          {
            title: "Automation",
            icon: Bot,
            children: [{ title: "New business automation", path: "/automation/new-business" }],
          },
        ],
      },
    ],
    entity: providerExecutiveEntityConfig,
  },
  billingAdmin: {
    key: "billingAdmin",
    label: "Billing Administrator (External)",
    dashboards: [
      newHomeDashboard,
      { key: "incomingClaims", name: "Incoming Claims & Status", path: "/incoming-claims", icon: WalletCards },
      requestsDashboard,
    ],
    sections: [
      {
        label: "Request Creation",
        items: [
          {
            title: "New Billing Claim",
            icon: FileText,
            children: [{ title: "Claim Submission Request", path: "/billing/claim-submission-request" }],
          },
        ],
      },
    ],
    entity: billingAdminEntityConfig,
  },
};

export const ROLE_LIST = Object.values(ROLE_CONFIGS);

export function findEntityOption(roleConfig: RoleConfig, optionId: string): RoleEntityOption | undefined {
  for (const group of roleConfig.entity.groups) {
    const match = group.options.find((option) => option.id === optionId);
    if (match) {
      return match;
    }
  }
  return undefined;
}

export function createInitialEntitySelections(): Record<RoleKey, string> {
  const entries = Object.values(ROLE_CONFIGS).map((config) => [config.key, config.entity.initialOptionId] as const);
  return Object.fromEntries(entries) as Record<RoleKey, string>;
}

export function getRoleConfig(role: RoleKey): RoleConfig {
  return ROLE_CONFIGS[role];
}
